import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const { signIn, signUp, signInWithGoogle, resetPassword, updatePassword } = useAuth();

  // Check multiple sources for password reset mode
  useEffect(() => {
    const checkResetMode = async () => {
      // Debug: Log all URL information
      console.log('Auth page loaded - URL debugging:');
      console.log('Full URL:', window.location.href);
      console.log('Search params:', window.location.search);
      console.log('Hash fragment:', window.location.hash);
      
      // Check URL search parameters
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      
      console.log('URL params type:', urlParams.get('type'));
      console.log('URL params mode:', urlParams.get('mode'));
      console.log('Hash params type:', hashParams.get('type'));
      console.log('Hash params access_token:', hashParams.get('access_token'));
      
      let resetModeDetected = false;
      
      // Method 1: Check URL parameters
      if (urlParams.get('type') === 'recovery' || urlParams.get('mode') === 'reset') {
        console.log('Reset mode detected via URL params');
        resetModeDetected = true;
      }
      
      // Method 2: Check hash fragments (common with OAuth flows)
      if (hashParams.get('type') === 'recovery' || hashParams.get('access_token')) {
        console.log('Reset mode detected via hash fragments');
        resetModeDetected = true;
      }
      
      // Method 3: Check if current session is a recovery session
      try {
        const { data: { session } } = await supabase.auth.getSession();
        console.log('Current session:', session);
        
        if (session?.user) {
          // Check if this is a recovery session by looking at recent auth events
          // Supabase sets user_metadata or app_metadata for recovery sessions
          const userMetadata = session.user.user_metadata;
          const appMetadata = session.user.app_metadata;
          
          console.log('User metadata:', userMetadata);
          console.log('App metadata:', appMetadata);
          
          // If user just signed in and we have recovery-related params anywhere, it's likely a reset
          if ((urlParams.has('type') || hashParams.has('type') || hashParams.has('access_token')) && session) {
            console.log('Reset mode detected via session + URL combination');
            resetModeDetected = true;
          }
        }
      } catch (err) {
        console.error('Error checking session:', err);
      }
      
      console.log('Final reset mode detection:', resetModeDetected);
      setIsResetMode(resetModeDetected);
      
      if (resetModeDetected) {
        toast.info('Please enter your new password below.');
      }
    };
    
    checkResetMode();
  }, []);

  // Also check searchParams from react-router (fallback)
  useEffect(() => {
    const reactRouterType = searchParams.get('type');
    const reactRouterMode = searchParams.get('mode');
    
    if (reactRouterType === 'recovery' || reactRouterMode === 'reset') {
      console.log('Reset mode detected via React Router search params');
      setIsResetMode(true);
      toast.info('Please enter your new password below.');
    }
  }, [searchParams]);

  const getNextPath = (): string | null => {
    const next = searchParams.get('next') || searchParams.get('redirect');
    return next && next.startsWith('/') && !next.startsWith('//') ? next : null;
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error } = await signIn(email, password);
    if (error) {
      setError(error.message);
    } else {
      toast.success('Successfully signed in!');
      const nextPath = getNextPath();
      if (nextPath) {
        navigate(nextPath);
      }
      // Otherwise navigation handled by useAuth hook
    }
    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // Persist desired post-auth destination across the email confirmation round-trip
    const nextPath = getNextPath();
    if (nextPath) {
      localStorage.setItem('pendingAuthRedirect', nextPath);
    }
    const { error } = await signUp(email, password, displayName);

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      toast.success('Check your email to confirm your account!');
      if (nextPath) {
        toast.info('After confirming your email, you\'ll go straight to creating your event.');
      }
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const { error } = await resetPassword(email);
    
    if (error) {
      setError(error.message);
    } else {
      toast.success('Password reset email sent! Check your inbox.');
      setShowForgotPassword(false);
      setEmail('');
    }
    setLoading(false);
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    const { error } = await updatePassword(newPassword);
    
    if (error) {
      setError(error.message);
    } else {
      toast.success('Password updated successfully!');
      navigate('/dashboard');
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    const nextPath = getNextPath();
    const {
      error
    } = await signInWithGoogle(nextPath || undefined);
    if (error) {
      setError(error.message);
      setLoading(false);
    }
    // Loading state will be cleared when auth state changes
  };
  // Show password reset form if user is in reset mode
  if (isResetMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/20 via-background to-secondary/20 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Reset Your Password
            </h1>
            <p className="text-muted-foreground mt-2">
              Enter your new password below
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Set New Password</CardTitle>
              <CardDescription>
                Choose a strong password for your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordUpdate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input 
                    id="new-password" 
                    type="password" 
                    value={newPassword} 
                    onChange={e => setNewPassword(e.target.value)} 
                    placeholder="Enter your new password" 
                    required 
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input 
                    id="confirm-password" 
                    type="password" 
                    value={confirmPassword} 
                    onChange={e => setConfirmPassword(e.target.value)} 
                    placeholder="Confirm your new password" 
                    required 
                    minLength={6}
                  />
                </div>
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" className="w-full min-h-[48px] text-base" disabled={loading}>
                  {loading ? 'Updating password...' : 'Update Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show forgot password form
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/20 via-background to-secondary/20 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Forgot Password?
            </h1>
            <p className="text-muted-foreground mt-2">
              Enter your email to receive reset instructions
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Reset Password</CardTitle>
              <CardDescription>
                We'll send you a link to reset your password
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email</Label>
                  <Input 
                    id="reset-email" 
                    type="email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    placeholder="Enter your email" 
                    required 
                  />
                </div>
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" className="w-full min-h-[48px] text-base" disabled={loading}>
                  {loading ? 'Sending reset email...' : 'Send Reset Email'}
                </Button>
                <Button 
                  type="button" 
                  variant="ghost" 
                  className="w-full min-h-[44px]" 
                  onClick={() => setShowForgotPassword(false)}
                >
                  Back to Sign In
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/20 via-background to-secondary/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Event Date Picker
          </h1>
          <p className="text-muted-foreground mt-2">
            Sign in to manage your events
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Welcome</CardTitle>
            <CardDescription>
              Sign in to your account or create a new one
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <div className="space-y-4">
                  <Button onClick={handleGoogleSignIn} variant="default" disabled={loading} className="w-full min-h-[48px] text-base">
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    {loading ? 'Signing in...' : 'Continue with Google'}
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        Or continue with email
                      </span>
                    </div>
                  </div>

                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <Input id="signin-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" required className="min-h-[44px] text-base" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="signin-password">Password</Label>
                        <Button
                          type="button"
                          variant="link"
                          className="px-0 font-normal text-sm text-muted-foreground hover:text-primary h-auto"
                          onClick={() => setShowForgotPassword(true)}
                        >
                          Forgot password?
                        </Button>
                      </div>
                      <Input id="signin-password" type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" required className="min-h-[44px] text-base" />
                    </div>
                    {error && <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>}
                    <Button type="submit" variant="outline" className="w-full min-h-[48px] text-base" disabled={loading}>
                      {loading ? 'Signing in...' : 'Sign In with Email'}
                    </Button>
                  </form>
                </div>
              </TabsContent>

              <TabsContent value="signup">
                <div className="space-y-4">
                  <Button onClick={handleGoogleSignIn} variant="default" className="w-full min-h-[48px] text-base" disabled={loading}>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    {loading ? 'Signing up...' : 'Continue with Google'}
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        Or continue with email
                      </span>
                    </div>
                  </div>

                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Display Name</Label>
                      <Input id="signup-name" type="text" autoComplete="name" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Enter your display name" className="min-h-[44px] text-base" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input id="signup-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" required className="min-h-[44px] text-base" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <Input id="signup-password" type="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a password" required className="min-h-[44px] text-base" />
                    </div>
                    

                    {error && <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>}
                    <Button type="submit" variant="outline" className="w-full min-h-[48px] text-base" disabled={loading}>
                      {loading ? 'Creating account...' : 'Create Account'}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground pt-2">
                      Free forever • 3 events/month • No credit card
                    </p>
                  </form>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
export default Auth;